import {
  Sparkles,
  Search,
  Bell,
  Users,
  Calendar,
  Shield,
  Building,
  CreditCard,
  BarChart3,
  ClipboardList,
  TrendingUp,
  Wallet,
  Megaphone,
  Package,
  Stethoscope,
  FileCheck,
  FlaskConical,
  BellRing,
  Clipboard,
  UserCheck,
  Receipt,
  ShieldCheck,
  ChevronRight,
  X,
} from 'lucide-react'
import type { TooltipRenderProps } from 'react-joyride'
import type { StepIcon } from '../lib/onboardingSteps'

const ICON_MAP: Record<StepIcon, React.ElementType> = {
  sparkles: Sparkles,
  search: Search,
  bell: Bell,
  users: Users,
  calendar: Calendar,
  shield: Shield,
  building: Building,
  'credit-card': CreditCard,
  'bar-chart': BarChart3,
  'clipboard-list': ClipboardList,
  'trending-up': TrendingUp,
  wallet: Wallet,
  megaphone: Megaphone,
  package: Package,
  stethoscope: Stethoscope,
  'file-check': FileCheck,
  'flask-conical': FlaskConical,
  'bell-ring': BellRing,
  clipboard: Clipboard,
  'user-check': UserCheck,
  receipt: Receipt,
  'shield-check': ShieldCheck,
}

const ICON_BG_MAP: Record<StepIcon, string> = {
  sparkles: 'bg-amber-50 text-amber-600',
  search: 'bg-sky-50 text-sky-600',
  bell: 'bg-rose-50 text-rose-600',
  users: 'bg-indigo-50 text-indigo-600',
  calendar: 'bg-emerald-50 text-emerald-600',
  shield: 'bg-slate-50 text-slate-600',
  building: 'bg-blue-50 text-blue-600',
  'credit-card': 'bg-violet-50 text-violet-600',
  'bar-chart': 'bg-teal-50 text-teal-600',
  'clipboard-list': 'bg-orange-50 text-orange-600',
  'trending-up': 'bg-green-50 text-green-600',
  wallet: 'bg-amber-50 text-amber-600',
  megaphone: 'bg-pink-50 text-pink-600',
  package: 'bg-cyan-50 text-cyan-600',
  stethoscope: 'bg-red-50 text-red-600',
  'file-check': 'bg-lime-50 text-lime-600',
  'flask-conical': 'bg-fuchsia-50 text-fuchsia-600',
  'bell-ring': 'bg-yellow-50 text-yellow-600',
  clipboard: 'bg-gray-50 text-gray-600',
  'user-check': 'bg-teal-50 text-teal-600',
  receipt: 'bg-purple-50 text-purple-600',
  'shield-check': 'bg-emerald-50 text-emerald-600',
}

export function OnboardingTooltip({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  size,
}: TooltipRenderProps) {
  const iconName = (step.data?.icon as StepIcon) || 'sparkles'
  const Icon = ICON_MAP[iconName] || Sparkles
  const iconBg = ICON_BG_MAP[iconName] || 'bg-teal-50 text-teal-600'
  const progress = size > 0 ? ((index + 1) / size) * 100 : 0
  const isFirst = index === 0
  const isLast = index === size - 1

  return (
    <div
      {...tooltipProps}
      className="onboarding-tooltip-enter relative w-[380px] overflow-hidden rounded-2xl border border-white/40 bg-white/95 shadow-[0_25px_80px_-12px_rgba(0,0,0,0.25)] backdrop-blur-xl"
    >
      {/* Progress bar */}
      <div className="absolute left-0 right-0 top-0 h-[3px] bg-slate-100">
        <div
          className="h-full rounded-r-full bg-teal-500 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Close button */}
      <button
        {...closeProps}
        className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
      >
        <X size={16} />
      </button>

      <div className="p-6 pt-7">
        {/* Header: Icon + Title */}
        <div className="mb-4 flex items-start gap-4">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconBg} shadow-sm`}
          >
            <Icon size={22} strokeWidth={1.8} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold leading-snug text-slate-900">
              {step.title}
            </h3>
            <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">
              {step.content}
            </p>
          </div>
        </div>

        {/* Footer: Step counter + Buttons */}
        <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
          {/* Step dots */}
          <div className="flex items-center gap-1.5">
            {size > 1 &&
              Array.from({ length: Math.min(size, 8) }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index
                      ? 'w-4 bg-teal-500'
                      : i < index
                        ? 'w-1.5 bg-teal-300'
                        : 'w-1.5 bg-slate-200'
                  }`}
                />
              ))}
            {size > 8 && (
              <span className="ml-1 text-[11px] font-medium text-slate-400">
                {index + 1}/{size}
              </span>
            )}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            {!isFirst && continuous && (
              <button
                {...backProps}
                className="rounded-lg px-3 py-2 text-[13px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
              >
                Anterior
              </button>
            )}

            {continuous && !isLast && (
              <button
                {...primaryProps}
                className="group flex items-center gap-1 rounded-lg bg-teal-600 px-4 py-2 text-[13px] font-semibold text-white shadow-sm shadow-teal-600/20 transition-all hover:bg-teal-700 hover:shadow-md hover:shadow-teal-600/25 active:scale-95"
              >
                Siguiente
                <ChevronRight
                  size={14}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </button>
            )}

            {continuous && isLast && (
              <button
                {...primaryProps}
                className="flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-[13px] font-semibold text-white shadow-sm shadow-teal-600/20 transition-all hover:bg-teal-700 hover:shadow-md hover:shadow-teal-600/25 active:scale-95"
              >
                <Sparkles size={14} />
                Finalizar
              </button>
            )}

            {!continuous && (
              <button
                {...primaryProps}
                className="rounded-lg bg-teal-600 px-4 py-2 text-[13px] font-semibold text-white shadow-sm shadow-teal-600/20 transition-all hover:bg-teal-700 active:scale-95"
              >
                OK
              </button>
            )}
          </div>
        </div>

        {/* Skip link */}
        {continuous && !isLast && (
          <div className="mt-2 flex justify-end">
            <button
              {...skipProps}
              className="text-[12px] font-medium text-slate-400 transition-colors hover:text-slate-600"
            >
              Saltar tour
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
